import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ScrollToTop from "react-scroll-to-top";
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Button,
  Container,
  Grid,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
import Loader from "react-js-loader";
import ProfileSettings from "./editForm/ProfileSettings";
import Membership from "./editForm/Membership";
import PasswordSettings from "./editForm/PasswordSettings";
import normalize from 'normalize-url';
import gravatar from 'gravatar';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import {ReactComponent as CreditCardIcon} from '../../assets/img/card.svg';
import {ReactComponent as PasswordIcon} from '../../assets/img/lock.svg';
import { getUserDetails, updateUserAvatar } from "../../redux/actions/user";
import { resizeUserAvatarImage, resizeThumbnailImage } from "../../utils/resizeFile";
import config from "../../config/config";
import "./AdminEditPage.style.css";

const MAX_FILE_SIZE = 5000000;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const AdminEditPage = () => {
  const { userId } = useParams();
  const dispatch = useDispatch();
  const theme = useTheme();

  const { loading, clientDetailData } = useSelector(state => ({
    loading: state.user.loading,
    clientDetailData: state.user.clientDetailData,
  }));
  const [currentTab, setCurrentTab] = useState('profile');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadFile, setUploadFile] = useState();
  const [avatarThumbnailFile, setAvatarThumbnailFile] = useState();

  useEffect(() => {
    dispatch(getUserDetails(userId));
  }, []);

  const handleIDCardUpload = () => {
    document.querySelector('#Avatar-Selection').value = null;
    document.querySelector('#Avatar-Selection').click();
  }

  const handleIDCardRemove = () => {
    setSelectedFile(null);
    const avatar = normalize(
			gravatar.url(clientDetailData.email, {
				s: '200',
				r: 'pg',
				d: 'mm'
			}),
			{ forceHttps: true }
		);
    setSelectedFile(avatar);
  }

  const handleSelectFile = async e => {
    const files = e.target.files;
    if (files.length === 0) {
      return;
    }
    if (!ACCEPTED_IMAGE_TYPES.includes(files?.[0]?.type)) {
      return;
    }
    if (files[0].size > MAX_FILE_SIZE) {
      return;
    }
    const file = files[0];
    const resizedFile = await resizeUserAvatarImage(file, file.type.split('/').pop().toUpperCase());
    setUploadFile(resizedFile);
    const thumbnailFile = await resizeThumbnailImage(file, file.type.split('/').pop().toUpperCase());
    setAvatarThumbnailFile(thumbnailFile);
    const reader = new FileReader();
    reader.readAsDataURL(resizedFile);
    reader.onloadend = (e) => {
      setSelectedFile(reader.result);
    };
  }

  const onChangeAvatar = () => {
    if (!selectedFile.includes("https://gravatar.com/") && uploadFile == null) {
      return;
    }
    let formData = new FormData();
    if (selectedFile.includes("https://gravatar.com/")) {
      formData.append('avatarUrl', selectedFile);
    } else {
      formData.append('avatar', uploadFile);
      formData.append('avatarThumbnail', avatarThumbnailFile);
    }
    dispatch(updateUserAvatar(formData));
  }

  return (
    <Container maxWidth="lg">
      <Grid
        item
        className="page-title"
        container
        justifyContent='space-between'
        sx={{ my: { sm: 2, md: 5 }, marginInline: 'auto' }}
      >
        <Grid item xs={12} sm={6}>
          <Typography sx={{ color: '#fff', fontSize: '1.5rem', fontWeight: 600 }}>Account Settings</Typography>
          <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 500, mt: '3px', opacity: 0.75 }}>Update user account details as Super Admin.</Typography>
        </Grid>
      </Grid>
      <Grid item container spacing={2}>
        <Grid item lg={4} sx={{ width: { xs: '100%'} }}>
          <Container
            direction='column'
            sx={{
              backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e2d',
              width: { md: '275px' },
              minHeight: '300px',
              borderRadius: 2,
              paddingY: 3,
            }}
          >
            {loading ? (
              <Grid marginTop="150px">
                <Loader type="bubble-loop" bgColor={"#2196f3"} color={'#2196f3'} size={100} />
              </Grid>
            ) : (
              <>
                <input type="file" name="idCard" id="Avatar-Selection" style={{ visibility: 'hidden' }} onChange={handleSelectFile} />
                <Grid textAlign="center">
                  <Box display="inline-flex" position="relative">
                    {selectedFile ?
                      selectedFile.includes("https://gravatar.com/") ?
                        <Avatar
                          sx={{
                            height: '150px',
                            width: '150px',
                            color: '#fff',
                            backgroundColor: 'tomato',
                            fontSize: '75px',
                            fontWeight: 600,
                            margin: 'auto',
                            border: '3px solid white',
                            boxShadow: 3,
                          }}
                        >
                          {clientDetailData?.firstName ? clientDetailData?.firstName[0] : ''}
                        </Avatar> :
                        <img
                          src={selectedFile}
                          className="Avatar-Image"
                          alt="avatarImageSelcted"
                        /> :
                      clientDetailData?.avatar?.includes("https://gravatar.com/") ?
                        <Avatar
                          sx={{
                            height: '150px',
                            width: '150px',
                            color: '#fff',
                            backgroundColor: 'tomato',
                            fontSize: '75px',
                            fontWeight: 600,
                            margin: 'auto',
                            border: '3px solid white',
                            boxShadow: 3,
                          }}
                        >
                          {clientDetailData?.firstName ? clientDetailData?.firstName[0] : ''}
                        </Avatar> :
                        <img
                          src={`${config.SERVER_BASE_URL}/${clientDetailData.avatar}`}
                          className="Avatar-Image"
                          alt="avatar"
                        />
                    }
                    <Tooltip title="Change Avatar" placement="top" enterTouchDelay={0}>
                      <span className="Avatar-upload" onClick={handleIDCardUpload}>
                        <i className="fa fa-pen"></i>
                      </span>
                    </Tooltip>
                    <Tooltip title="Change Avatar" placement="top" enterTouchDelay={0}>
                      <span className="Avatar-remove " onClick={handleIDCardRemove}>
                        <i className="fa fa-remove"></i>
                      </span>
                    </Tooltip>
                  </Box>
                </Grid>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, textAlign: 'center', mt: 3 }}>{clientDetailData?.firstName + ' ' + clientDetailData?.lastName}</Typography>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, textAlign: 'center', mt: 1, color: '#A1A5B7' }}>ID #{clientDetailData?.bookFormId}</Typography>
                <Grid sx={{ textAlign: 'center', mt: 3 }}>
                  {clientDetailData?.membership === 0
                    ? <Box className="booking-detail-type" sx={{ color: '#FFC700', backgroundColor: theme.palette.mode ==="light" ? "#FFF8DD" : "#392F28", fontWeight: 'bold' }}>Trial User</Box>
                    : clientDetailData?.membership === 1
                      ? <Box className="booking-detail-type" sx={{ color: '#F1416C', backgroundColor: theme.palette.mode ==="light" ? "#FFF5F8" : "#3A2434", fontWeight: 'bold' }}>Expired User</Box>
                      : <Box className="booking-detail-type" sx={{ color: '#50CD89', backgroundColor: theme.palette.mode === 'light' ? "#E8FFF3" : "#1C3238", fontWeight: 'bold' }}>Pro User</Box>
                  }
                </Grid>
                <Box sx={{ fontSize: '0.8rem', mt: 3, paddingX: '8px' }}>
                  <Button
                    fullWidth
                    onClick={() => setCurrentTab('profile')}
                    sx={{
                      backgroundColor: theme.palette.mode === 'light' ?
                        currentTab === 'profile' ? '#f4f6fa' : 'white' :
                        currentTab === 'profile' ? '#2b2b40' : '#1e1e2d',
                      color: theme.palette.mode === 'light' ?
                        currentTab === 'profile' ? '#2196f3' : '#A1A5B7' :
                        currentTab === 'profile' ? '#009cf7' : '#565674',
                      "&:hover": {
                        color: theme.palette.mode === 'light' ? '#2196f3' : '#009cf7',
                        backgroundColor: theme.palette.mode === 'light' ? '#f4f6fa' : '#2b2b40',
                      },
                      "&:hover p": {
                        color: theme.palette.mode === 'light' ? '#2196f3' : '#009cf7',
                      },
                      fontWeight: 600,
                      textTransform: 'none',
                      padding: '9px 13px',
                      borderRadius: '6px',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <AccountCircleIcon />
                    &nbsp;&nbsp;&nbsp;
                    <Typography
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: theme.palette.mode === 'light' ?
                          currentTab === 'profile' ? '#2196f3' : '#181c32' :
                          currentTab === 'profile' ? '#009cf7' : '#fff'
                      }}
                    >
                      My Profile
                    </Typography>
                  </Button>
                </Box>
                <Box sx={{ fontSize: '0.8rem', mt: 2, paddingX: '8px' }}>
                  <Button
                    fullWidth
                    onClick={() => setCurrentTab('membership')}
                    sx={{
                      backgroundColor: theme.palette.mode === 'light' ?
                        currentTab === 'membership' ? '#f4f6fa' : 'white' :
                        currentTab === 'membership' ? '#2b2b40' : '#1e1e2d',
                      color: theme.palette.mode === 'light' ?
                        currentTab === 'membership' ? '#2196f3' : '#A1A5B7' :
                        currentTab === 'membership' ? '#009cf7' : '#565674',
                      "&:hover": {
                        color: theme.palette.mode === 'light' ? '#2196f3' : '#009cf7',
                        backgroundColor: theme.palette.mode === 'light' ? '#f4f6fa' : '#2b2b40',
                      },
                      "&:hover p": {
                        color: theme.palette.mode === 'light' ? '#2196f3' : '#009cf7',
                      },
                      fontWeight: 600,
                      textTransform: 'none',
                      padding: '9px 13px',
                      borderRadius: '6px',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <CreditCardIcon />&nbsp;&nbsp;&nbsp;
                    <Typography
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: theme.palette.mode === 'light' ?
                          currentTab === 'membership' ? '#2196f3' : '#181c32' :
                          currentTab === 'membership' ? '#009cf7' : '#fff'
                      }}
                    >
                      My Membership
                    </Typography>
                  </Button>
                </Box>
                <Box sx={{ fontSize: '0.8rem', mt: 2, mb: 5, paddingX: '8px' }}>
                  <Button
                    fullWidth
                    onClick={() => setCurrentTab('password')}
                    sx={{
                      backgroundColor: theme.palette.mode === 'light' ?
                        currentTab === 'password' ? '#f4f6fa' : 'white' :
                        currentTab === 'password' ? '#2b2b40' : '#1e1e2d',
                      color: theme.palette.mode === 'light' ?
                        currentTab === 'password' ? '#2196f3' : '#A1A5B7' :
                        currentTab === 'password' ? '#009cf7' : '#565674',
                      "&:hover": {
                        color: theme.palette.mode === 'light' ? '#2196f3' : '#009cf7',
                        backgroundColor: theme.palette.mode === 'light' ? '#f4f6fa' : '#2b2b40',
                      },
                      "&:hover p": {
                        color: theme.palette.mode === 'light' ? '#2196f3' : '#009cf7',
                      },
                      fontWeight: 600,
                      textTransform: 'none',
                      padding: '9px 13px',
                      borderRadius: '6px',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <PasswordIcon />&nbsp;&nbsp;&nbsp;
                    <Typography
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: theme.palette.mode === 'light' ?
                          currentTab === 'password' ? '#2196f3' : '#181c32' :
                          currentTab === 'password' ? '#009cf7' : '#fff'
                      }}
                    >
                      My Password
                    </Typography>
                  </Button>
                </Box>
              </>
            )}
          </Container>
        </Grid>
        <Grid item container lg={8} sx={{ minHeight: '300px', borderRadius: 2 }}>
          <Container
            direction='column'
            sx={{
              padding: { xs: '24px !important', md: '40px !important'},
              backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e2d',
              minHeight: '300px',
              borderRadius: 2
            }}>
            {loading ? (
              <Grid marginTop="150px">
                <Loader type="bubble-loop" bgColor={"#2196f3"} color={'#2196f3'} size={100} />
              </Grid>
            ) : (
              <>
                {currentTab === 'profile' ?
                  <ProfileSettings userId={userId} updateUserAvatar={onChangeAvatar} />
                  : currentTab === 'membership' ?
                    <Membership userId={userId} /> :
                    currentTab === 'password' ?
                      <PasswordSettings userId={userId} /> :
                      null
                }
              </>
            )}
          </Container>
        </Grid>
      </Grid>
      <ScrollToTop
        smooth
        color="#009EF7"
      // component={<Button startIcon={<NorthIcon sx={{ color: '#fff' }} />} sx={{ padding: 0 }} variant="contained" />} style={{ padding: 0 }}
      />
    </Container>
  );
}

export default AdminEditPage;
